import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Scan, Package, User, Mail, MapPin, AlertCircle, CheckCircle2, Box, Barcode, ExternalLink, AlertTriangle, ArrowRight, Clock, Plus, Sparkles } from 'lucide-react';
import { Card, Input, Badge, LoadingSpinner, Notification, Button } from './SharedComponents';

const API_URL = import.meta.env.VITE_API_URL || 'https://react-app-production-3114.up.railway.app';

// Format date to friendly relative time or short date
const formatFriendlyTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return diffMinutes <= 1 ? 'Just now' : `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  } else {
    // Format as short date for older dates
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const ReceivingScanner = () => {
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [scanHistory, setScanHistory] = useState([]);
  const [submitting, setSubmitting] = useState(null); // inbound_id being submitted
  const [addResult, setAddResult] = useState(null); // Result after adding to inbound
  const [aiSearching, setAiSearching] = useState(false); // AI matching in progress
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Analyze inbound matches to determine confidence levels (including AI matches)
  const inboundAnalysis = useMemo(() => {
    if (!searchResult?.items) return { inbounds: [], bestMatch: null, totalItems: 0, hasAiMatches: false };

    const inboundMap = new Map();
    let hasAiMatches = false;

    searchResult.items.forEach((item, itemIndex) => {
      // Process exact ASIN matches
      if (item.inbound_matches) {
        item.inbound_matches.forEach(match => {
          if (!inboundMap.has(match.inbound_id)) {
            inboundMap.set(match.inbound_id, {
              ...match,
              matchingItems: [],
              matchingItemIndices: new Set()
            });
          }
          const inbound = inboundMap.get(match.inbound_id);
          inbound.matchingItems.push({
            itemIndex,
            item,
            matchData: match,
            isAiMatch: false,
            // Calculate what happens after adding this item
            qtyToAdd: parseInt(item.quantity) || 1,
            currentReceived: parseInt(match.ACTUAL) || 0,
            expected: parseInt(match.EXPECTED) || 0,
          });
          inbound.matchingItemIndices.add(itemIndex);
        });
      }

      // Process AI matches (only for items WITHOUT exact ASIN matches)
      if (item.ai_matches && item.ai_matches.length > 0 && (!item.inbound_matches || item.inbound_matches.length === 0)) {
        hasAiMatches = true;
        item.ai_matches.forEach(aiMatch => {
          // Find or create the inbound entry using the matched inbound info
          // V2 uses matched_inbound_id, V1 uses inbound_id
          const inboundId = aiMatch.matched_inbound_id || aiMatch.inbound_id || `ai_${aiMatch.matched_asin}`;

          if (!inboundMap.has(inboundId)) {
            inboundMap.set(inboundId, {
              inbound_id: inboundId,
              // V2 uses matched_inbound_name, V1 uses inbound_name
              inbound_name: aiMatch.matched_inbound_name || aiMatch.inbound_name || `AI Match: ${aiMatch.matched_sku}`,
              inbound_status: aiMatch.inbound_status || 'open',
              inbound_created_at: aiMatch.inbound_created_at || null,
              matchingItems: [],
              matchingItemIndices: new Set(),
              isAiInbound: true
            });
          }
          const inbound = inboundMap.get(inboundId);

          // Calculate adjusted quantity for multipack matches
          const baseQty = parseInt(item.quantity) || 1;
          const adjustedQty = aiMatch.match_type === 'multipack'
            ? Math.floor((baseQty * aiMatch.output_qty) / aiMatch.input_qty)
            : baseQty;

          inbound.matchingItems.push({
            itemIndex,
            item,
            matchData: {
              ASIN: aiMatch.matched_asin,
              SKU: aiMatch.matched_sku,
              TITLE: aiMatch.matched_title,
              EXPECTED: aiMatch.matched_expected || 0,
              ACTUAL: aiMatch.matched_actual || 0,
            },
            isAiMatch: true,
            aiMatchData: aiMatch,
            qtyToAdd: adjustedQty,
            currentReceived: aiMatch.matched_actual || 0,
            expected: aiMatch.matched_expected || 0,
          });
          inbound.matchingItemIndices.add(itemIndex);
        });
      }
    });

    const totalItems = searchResult.items.length;
    const inbounds = Array.from(inboundMap.values()).map(inbound => {
      // Calculate totals for this inbound
      const totalExpected = inbound.matchingItems.reduce((sum, mi) => sum + mi.expected, 0);
      const totalCurrentReceived = inbound.matchingItems.reduce((sum, mi) => sum + mi.currentReceived, 0);
      const totalToAdd = inbound.matchingItems.reduce((sum, mi) => sum + mi.qtyToAdd, 0);
      const totalAfterAdding = totalCurrentReceived + totalToAdd;
      const willBeComplete = totalAfterAdding >= totalExpected;
      const remaining = Math.max(0, totalExpected - totalAfterAdding);
      const hasAiItems = inbound.matchingItems.some(mi => mi.isAiMatch);

      // Check if ALL items in the box can fit (no item will over-receive)
      const allItemsFit = inbound.matchingItems.every(mi => {
        const afterAdding = mi.currentReceived + mi.qtyToAdd;
        return afterAdding <= mi.expected;
      });

      return {
        ...inbound,
        matchCount: inbound.matchingItems.length,
        matchPercentage: (inbound.matchingItems.length / totalItems) * 100,
        isFullMatch: inbound.matchingItems.length === totalItems,
        isPartialMatch: inbound.matchingItems.length < totalItems && inbound.matchingItems.length > 0,
        hasAiItems,
        // Quantity calculations
        totalExpected,
        totalCurrentReceived,
        totalToAdd,
        totalAfterAdding,
        willBeComplete,
        remaining,
        // New: whether all items fit without over-receiving
        allItemsFit
      };
    });

    // Sort inbounds with prioritization:
    // 1. Full match (all box items match) AND all items fit (no over-receiving) - highest priority
    // 2. Among those, sort by creation time (most recent first)
    // 3. Then partial matches that fit
    // 4. Then matches that would over-receive
    inbounds.sort((a, b) => {
      // Priority 1: Full match with all items fitting vs not
      const aIdeal = a.isFullMatch && a.allItemsFit;
      const bIdeal = b.isFullMatch && b.allItemsFit;
      if (aIdeal && !bIdeal) return -1;
      if (!aIdeal && bIdeal) return 1;

      // Priority 2: If both are ideal or both are not, prefer ones where items fit
      if (a.allItemsFit && !b.allItemsFit) return -1;
      if (!a.allItemsFit && b.allItemsFit) return 1;

      // Priority 3: More items matching is better
      if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;

      // Priority 4: More recently created inbound (by inbound_created_at if available)
      const aTime = a.inbound_created_at ? new Date(a.inbound_created_at).getTime() : 0;
      const bTime = b.inbound_created_at ? new Date(b.inbound_created_at).getTime() : 0;
      return bTime - aTime; // Most recent first
    });

    // Best match is the first one that is a full match with items fitting, or just the first full match, or single option
    const bestMatch = inbounds.find(i => i.isFullMatch && i.allItemsFit)
      || inbounds.find(i => i.isFullMatch)
      || (inbounds.length === 1 ? inbounds[0] : null);

    return { inbounds, bestMatch, totalItems, hasAiMatches };
  }, [searchResult]);

  // Fetch AI matches for items without exact matches
  const fetchAIMatches = async (items, clientId) => {
    try {
      setAiSearching(true);

      // Prepare items for AI matching (include order_number for inbound name matching)
      const itemsForAI = items
        .filter(item => item.needs_ai_match)
        .map(item => ({
          asin: item.asin,
          name: item.item_name,
          quantity: item.quantity,
          image_url: item.image_url,
          order_number: item.order_number // Used to match against inbound names
        }));

      if (itemsForAI.length === 0) {
        setAiSearching(false);
        return;
      }

      console.log(`[AI Search] Searching for ${itemsForAI.length} items...`);

      const response = await fetch(`${API_URL}/api/receiving-scanner/ai-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsForAI, clientId })
      });

      const data = await response.json();

      if (response.ok && data.matches && data.matches.length > 0) {
        console.log(`[AI Search] Found ${data.matches.length} AI matches`);

        // Update searchResult with AI matches
        setSearchResult(prev => {
          if (!prev) return prev;

          const updatedItems = prev.items.map(item => {
            const aiMatchesForItem = data.matches.filter(m => m.scanned_asin === item.asin);
            if (aiMatchesForItem.length > 0) {
              return {
                ...item,
                ai_matches: aiMatchesForItem,
                needs_ai_match: false
              };
            }
            return item;
          });

          return {
            ...prev,
            items: updatedItems,
            has_ai_matches: true
          };
        });

        setNotification({
          show: true,
          message: `AI found ${data.matches.length} potential match(es)`,
          type: 'success'
        });
      } else {
        console.log('[AI Search] No AI matches found');
        // Mark items as searched (no longer needs_ai_match)
        setSearchResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map(item => ({
              ...item,
              needs_ai_match: false,
              ai_search_complete: true
            }))
          };
        });
      }
    } catch (err) {
      console.error('[AI Search] Error:', err);
    } finally {
      setAiSearching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const trimmedCode = trackingCode.trim();
    if (!trimmedCode) {
      setError('Please enter a tracking code');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResult(null);
    setAiSearching(false);

    try {
      const response = await fetch(`${API_URL}/api/receiving-scanner/search?tracking=${encodeURIComponent(trimmedCode)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to search tracking code');
      }

      if (data.found) {
        setSearchResult(data);
        setNotification({
          show: true,
          message: `Found ${data.items.length} item(s) for ${data.client?.name || 'Unknown Client'}`,
          type: 'success'
        });
        setScanHistory(prev => [{
          trackingCode: trimmedCode,
          clientName: data.client?.name || 'Unknown',
          itemCount: data.items.length,
          timestamp: new Date().toLocaleTimeString()
        }, ...prev.slice(0, 9)]);

        // If there are items needing AI matching, start that in the background
        if (data.needs_ai_matching && data.client?.id) {
          // Small delay to let UI render first
          setTimeout(() => {
            fetchAIMatches(data.items, data.client.id);
          }, 100);
        }
      } else {
        setError('No items found for this tracking code');
        setNotification({ show: true, message: 'Tracking code not found in system', type: 'warning' });
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'An error occurred while searching');
      setNotification({ show: true, message: err.message || 'Search failed', type: 'error' });
    } finally {
      setLoading(false);
      setTrackingCode('');
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const clearResults = () => {
    setSearchResult(null);
    setError(null);
    setAddResult(null);
    setAiSearching(false);
    if (inputRef.current) inputRef.current.focus();
  };

  // Handle adding items to an inbound
  const handleAddToInbound = async (inbound) => {
    setSubmitting(inbound.inbound_id);
    setAddResult(null);

    try {
      // Prepare items array for the API
      const itemsToAdd = inbound.matchingItems.map(mi => ({
        chrome_ext_id: mi.item.id,
        asin: mi.item.asin,
        quantity: mi.qtyToAdd,
        sku: mi.matchData?.SKU
      }));

      const response = await fetch(`${API_URL}/api/receiving-scanner/add-to-inbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inbound_id: inbound.inbound_id,
          tracking_code: searchResult.trackingCode,
          items: itemsToAdd
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAddResult({
          inbound_id: inbound.inbound_id,
          inbound_name: inbound.inbound_name,
          ...data
        });
        setNotification({
          show: true,
          message: `Added ${data.items_added} item(s) to ${inbound.inbound_name}${data.inbound_complete ? ' - Inbound complete!' : ''}`,
          type: 'success'
        });
      } else {
        setNotification({
          show: true,
          message: data.error || 'Failed to add items to inbound',
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error adding to inbound:', err);
      setNotification({
        show: true,
        message: 'Error adding items to inbound',
        type: 'error'
      });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, show: false })}
      />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Scan className="h-7 w-7" />
          Receiving Scanner
        </h1>
        <p className="text-gray-600 mt-1">Scan or enter tracking codes to identify incoming packages</p>
      </div>

      {/* Search Input */}
      <Card className="p-4 md:p-6 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              ref={inputRef}
              placeholder="Scan or enter tracking code..."
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value)}
              icon={Barcode}
              className="text-lg py-3"
              autoFocus
            />
          </div>
          <Button type="submit" disabled={loading || !trackingCode.trim()} className="px-8">
            {loading ? <LoadingSpinner size="sm" /> : 'Search'}
          </Button>
        </form>
        <p className="text-sm text-gray-500 mt-2">Press Enter after scanning or typing to search</p>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card className="p-8 mb-6 flex items-center justify-center">
          <LoadingSpinner size="lg" />
          <span className="ml-4 text-gray-600">Searching...</span>
        </Card>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card className="p-6 mb-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800">Not Found</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Search Results */}
      {searchResult && !loading && (
        <div className="space-y-4">
          {/* Client Information */}
          {searchResult.client && (
            <Card className="p-4 md:p-6 border-green-200 bg-green-50">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 p-3 rounded-full flex-shrink-0">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 break-words">{searchResult.client.name}</h2>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span className="break-all">{searchResult.client.email}</span>
                      </div>
                      {searchResult.client.client_name && (
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span>{searchResult.client.client_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Badge variant="success" className="text-sm flex-shrink-0">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Client Verified
                </Badge>
              </div>
            </Card>
          )}

          {/* Match Summary Banner */}
          {inboundAnalysis.inbounds.length > 0 && (
            <Card className={`p-4 ${
              inboundAnalysis.bestMatch?.isFullMatch ? 'border-green-300 bg-green-50' :
              inboundAnalysis.bestMatch ? 'border-blue-300 bg-blue-50' :
              'border-yellow-300 bg-yellow-50'
            }`}>
              <div className="flex items-start gap-3">
                {inboundAnalysis.bestMatch?.isFullMatch ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  {inboundAnalysis.bestMatch?.isFullMatch ? (
                    <>
                      <span className="font-semibold text-green-800">Perfect Match Found!</span>
                      <p className="text-green-700 text-sm mt-1">
                        All {inboundAnalysis.totalItems} items match: <strong>{inboundAnalysis.bestMatch.inbound_name}</strong>
                      </p>
                    </>
                  ) : inboundAnalysis.bestMatch ? (
                    <>
                      <span className="font-semibold text-blue-800">Likely Match</span>
                      <p className="text-blue-700 text-sm mt-1">
                        {inboundAnalysis.bestMatch.matchCount}/{inboundAnalysis.totalItems} items match: <strong>{inboundAnalysis.bestMatch.inbound_name}</strong>
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-yellow-800">Multiple Possible Inbounds</span>
                      <p className="text-yellow-700 text-sm mt-1">
                        Items match {inboundAnalysis.inbounds.length} different inbounds - verify manually
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Data Source Indicator */}
          {searchResult.data_source && (
            <div className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 ${
              searchResult.data_source === 'live'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : searchResult.data_source === 'partial'
                  ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                searchResult.data_source === 'live' ? 'bg-green-500' :
                searchResult.data_source === 'partial' ? 'bg-yellow-500' : 'bg-gray-400'
              }`}></span>
              {searchResult.data_source === 'live' && 'Live data from YYZ Prep'}
              {searchResult.data_source === 'partial' && 'Partial live data (some inbounds unavailable)'}
              {searchResult.data_source === 'local' && 'Cached data (YYZ API unavailable)'}
              {searchResult.fetched_at && (
                <span className="text-[10px] opacity-75 ml-2">
                  as of {new Date(searchResult.fetched_at).toLocaleTimeString()}
                </span>
              )}
            </div>
          )}

          {/* Two Column Layout: Items and Inbounds */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Items in Package */}
            <Card className="p-4">
              {/* Calculate unique orders */}
              {(() => {
                const uniqueOrders = [...new Set(searchResult.items.map(i => i.order_number).filter(Boolean))];
                const hasMultipleOrders = uniqueOrders.length > 1;
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Items in Package
                      </h3>
                      <div className="flex items-center gap-2">
                        {hasMultipleOrders && (
                          <Badge variant="warning">{uniqueOrders.length} orders</Badge>
                        )}
                        <Badge variant="info">{searchResult.items.length} item(s)</Badge>
                      </div>
                    </div>
                    {hasMultipleOrders && (
                      <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                        <strong>Mixed Box:</strong> Items from {uniqueOrders.length} different orders combined in this shipment
                      </div>
                    )}
                  </>
                );
              })()}

              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {searchResult.items.map((item, index) => {
                  const hasMatches = item.inbound_matches && item.inbound_matches.length > 0;
                  const matchedInboundIds = item.inbound_matches?.map(m => m.inbound_id) || [];
                  const isBestMatchItem = inboundAnalysis.bestMatch && matchedInboundIds.includes(inboundAnalysis.bestMatch.inbound_id);

                  return (
                    <div
                      key={item.id || index}
                      className={`border rounded-lg p-3 ${
                        !hasMatches ? 'border-red-300 bg-red-50' :
                        isBestMatchItem && inboundAnalysis.bestMatch?.isFullMatch ? 'border-green-300 bg-green-50' :
                        'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.item_name}
                            className="w-14 h-14 object-contain rounded border border-gray-200 flex-shrink-0 bg-white"
                          />
                        ) : (
                          <div className="bg-gray-100 rounded w-14 h-14 flex items-center justify-center flex-shrink-0">
                            <Box className="h-7 w-7 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm leading-tight line-clamp-2">{item.item_name}</h4>
                          <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-gray-600">
                              ASIN: <span className="font-mono font-medium text-gray-800">{item.asin}</span>
                            </span>
                            {item.order_number && (
                              <span className="text-xs text-gray-500">
                                Order: <span className="font-mono">{item.order_number}</span>
                              </span>
                            )}
                            <div className="flex items-center gap-1 bg-blue-100 border-2 border-blue-300 rounded-lg px-3 py-1">
                              <span className="text-xs text-blue-700 font-medium">QTY:</span>
                              <span className="text-2xl font-black text-blue-800">{item.quantity}</span>
                            </div>
                          </div>

                          {/* Match Status */}
                          <div className="mt-2">
                            {hasMatches ? (
                              <div className="flex flex-wrap gap-1">
                                {item.inbound_matches.map((match, mi) => {
                                  const isBest = inboundAnalysis.bestMatch?.inbound_id === match.inbound_id;
                                  const inboundData = inboundAnalysis.inbounds.find(i => i.inbound_id === match.inbound_id);
                                  return (
                                    <span
                                      key={mi}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                        isBest && inboundData?.isFullMatch ? 'bg-green-200 text-green-800' :
                                        isBest ? 'bg-blue-200 text-blue-800' :
                                        'bg-yellow-200 text-yellow-800'
                                      }`}
                                    >
                                      <ArrowRight className="h-3 w-3" />
                                      {match.inbound_name.length > 20 ? match.inbound_name.slice(0, 20) + '...' : match.inbound_name}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : item.ai_matches && item.ai_matches.length > 0 ? (
                              /* AI Match Display - Simplified with clear quantity mapping */
                              <div className="space-y-2">
                                {item.ai_matches.map((aiMatch, ai) => {
                                  // Calculate the converted quantity
                                  const boxQty = parseInt(item.quantity) || 1;
                                  const inputQty = aiMatch.input_qty || 1;
                                  const outputQty = aiMatch.output_qty || 1;
                                  const convertedUnits = Math.floor((boxQty * outputQty) / inputQty);
                                  const isMultipack = aiMatch.match_type === 'multipack' && (inputQty !== 1 || outputQty !== 1);

                                  return (
                                    <div
                                      key={ai}
                                      className={`border-2 rounded-lg p-3 ${
                                        aiMatch.confidence >= 0.9 ? 'border-green-400 bg-green-50' :
                                        aiMatch.confidence >= 0.7 ? 'border-yellow-400 bg-yellow-50' :
                                        'border-orange-400 bg-orange-50'
                                      }`}
                                    >
                                      {/* Header row: AI badge + confidence + inbound name */}
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <Sparkles className="h-4 w-4 text-purple-500" />
                                          <span className="text-sm font-semibold text-purple-700">AI Match</span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                            aiMatch.confidence >= 0.9 ? 'bg-green-200 text-green-800' :
                                            aiMatch.confidence >= 0.7 ? 'bg-yellow-200 text-yellow-800' :
                                            'bg-orange-200 text-orange-800'
                                          }`}>
                                            {Math.round(aiMatch.confidence * 100)}%
                                          </span>
                                          {/* Order ID Match indicator */}
                                          {aiMatch.v2_metadata?.order_id_match && (
                                            <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded font-bold">
                                              ORDER ID
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-xs text-purple-600 font-medium truncate max-w-[150px]">
                                          → {aiMatch.matched_inbound_name || 'Inbound'}
                                        </span>
                                      </div>

                                      {/* Product info row */}
                                      <div className="flex items-center gap-2 mb-2">
                                        {aiMatch.matched_image && (
                                          <img
                                            src={aiMatch.matched_image}
                                            alt=""
                                            className="w-10 h-10 object-contain rounded border border-gray-200 bg-white flex-shrink-0"
                                          />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs text-gray-700 line-clamp-1">
                                            {aiMatch.matched_title || 'Unknown Product'}
                                          </p>
                                          <p className="text-[10px] text-gray-500 font-mono">
                                            {aiMatch.matched_asin}
                                          </p>
                                        </div>
                                      </div>

                                      {/* QUANTITY CONVERSION - The key info */}
                                      <div className="bg-white rounded p-2 border border-gray-200">
                                        {isMultipack ? (
                                          <div className="flex items-center justify-between">
                                            <div className="text-center">
                                              <div className="text-lg font-bold text-gray-800">{boxQty}</div>
                                              <div className="text-[10px] text-gray-500 uppercase">In Box</div>
                                            </div>
                                            <div className="flex items-center gap-1 px-2">
                                              <span className="text-xs text-gray-400">×</span>
                                              <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-bold">
                                                {outputQty}/{inputQty}
                                              </div>
                                              <span className="text-xs text-gray-400">=</span>
                                            </div>
                                            <div className="text-center">
                                              <div className="text-lg font-bold text-green-600">{convertedUnits}</div>
                                              <div className="text-[10px] text-gray-500 uppercase">Units to Add</div>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-center gap-2">
                                            <span className="text-sm text-gray-600">Adding</span>
                                            <span className="text-lg font-bold text-green-600">{boxQty}</span>
                                            <span className="text-sm text-gray-600">units (1:1)</span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Brief reason if multipack */}
                                      {isMultipack && aiMatch.reasoning && (
                                        <p className="text-[10px] text-gray-500 mt-1 text-center italic">
                                          {aiMatch.reasoning}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : item.needs_ai_match && aiSearching ? (
                              /* AI Search in Progress */
                              <div className="flex items-center gap-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                                <span className="text-sm text-purple-700 font-medium">
                                  Searching with AI for a match...
                                </span>
                              </div>
                            ) : item.ai_search_complete ? (
                              /* AI search completed but no match found */
                              <span className="inline-flex items-center gap-1 text-orange-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                No match found (AI searched)
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                                <AlertCircle className="h-3 w-3" />
                                No inbound match found
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-gray-600 text-sm">Total Units in Package:</span>
                <span className="text-lg font-bold text-gray-900">
                  {searchResult.items.reduce((sum, item) => sum + parseInt(item.quantity || 1), 0)}
                </span>
              </div>
            </Card>

            {/* Right: Matched Inbounds with Before/After */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Matched Inbounds
                </h3>
                <Badge variant={inboundAnalysis.inbounds.length > 0 ? 'success' : 'warning'}>
                  {inboundAnalysis.inbounds.length} match(es)
                </Badge>
              </div>

              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
                {/* AI Searching Banner */}
                {aiSearching && (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg mb-2">
                    <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-purple-700">AI is searching for matches...</p>
                      <p className="text-xs text-purple-600">Looking for products without exact ASIN matches</p>
                    </div>
                  </div>
                )}

                {inboundAnalysis.inbounds.length === 0 && !aiSearching ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                    <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No matching inbounds found</p>
                    <p className="text-gray-400 text-sm mt-1">Items may not be in the system yet</p>
                  </div>
                ) : inboundAnalysis.inbounds.length === 0 && aiSearching ? (
                  <div className="border-2 border-dashed border-purple-200 rounded-lg p-8 text-center bg-purple-50">
                    <Sparkles className="h-10 w-10 text-purple-400 mx-auto mb-3" />
                    <p className="text-purple-600 font-medium">No exact matches found</p>
                    <p className="text-purple-500 text-sm mt-1">AI is searching for potential matches...</p>
                  </div>
                ) : (
                  inboundAnalysis.inbounds.map((inbound) => {
                    const isBestMatch = inboundAnalysis.bestMatch?.inbound_id === inbound.inbound_id;
                    const isPartialWarning = !inbound.isFullMatch && inboundAnalysis.inbounds.length > 1 && !isBestMatch;

                    return (
                      <div
                        key={inbound.inbound_id}
                        className={`border-2 rounded-lg overflow-hidden ${
                          inbound.isFullMatch && isBestMatch
                            ? 'border-green-400 ring-2 ring-green-200 ring-offset-1'
                            : isBestMatch
                              ? 'border-blue-400 ring-2 ring-blue-200 ring-offset-1'
                              : isPartialWarning
                                ? 'border-yellow-300 opacity-70'
                                : 'border-gray-200'
                        }`}
                      >
                        {/* Header */}
                        <div className={`p-3 ${
                          inbound.isFullMatch && isBestMatch ? 'bg-green-50' :
                          isBestMatch ? 'bg-blue-50' :
                          isPartialWarning ? 'bg-yellow-50/50' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-900 break-words">
                                  {inbound.inbound_name}
                                </span>
                                <Badge variant={
                                  inbound.inbound_status === 'received' ? 'success' :
                                  inbound.inbound_status === 'shipped' ? 'info' : 'warning'
                                }>
                                  {inbound.inbound_status}
                                </Badge>
                                {/* Inbound creation time */}
                                {inbound.inbound_created_at && (
                                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                    <Clock className="h-3 w-3" />
                                    {formatFriendlyTime(inbound.inbound_created_at)}
                                  </span>
                                )}
                              </div>
                              {/* Match count */}
                              <div className={`mt-1 text-sm ${
                                inbound.isFullMatch ? 'text-green-700' :
                                isBestMatch ? 'text-blue-700' : 'text-yellow-700'
                              }`}>
                                {inbound.isFullMatch ? (
                                  <span className="flex items-center gap-1">
                                    <CheckCircle2 className="h-4 w-4" />
                                    All {inbound.matchCount} items match
                                    {inbound.hasAiItems && (
                                      <span className="inline-flex items-center gap-1 ml-2 text-purple-600">
                                        <Sparkles className="h-3 w-3" />
                                        AI
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <AlertTriangle className="h-4 w-4" />
                                    {inbound.matchCount}/{inboundAnalysis.totalItems} items match
                                    {inbound.hasAiItems && (
                                      <span className="inline-flex items-center gap-1 ml-2 text-purple-600">
                                        <Sparkles className="h-3 w-3" />
                                        AI
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Link
                              to={`/inbounds/${inbound.inbound_id}`}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </Link>
                          </div>

                          {/* Warning for partial matches */}
                          {isPartialWarning && (
                            <div className="mt-2 text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 px-2 py-1.5 rounded">
                              ⚠️ Not all items match - probably not this inbound
                            </div>
                          )}
                        </div>

                        {/* Before/After Breakdown */}
                        <div className="p-3 bg-white">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Quantity Breakdown
                          </div>

                          {/* Per-item breakdown */}
                          <div className="space-y-2">
                            {inbound.matchingItems.map((mi, idx) => {
                              const afterAdding = mi.currentReceived + mi.qtyToAdd;
                              const willComplete = afterAdding >= mi.expected;
                              const overReceiving = afterAdding > mi.expected;
                              const remaining = Math.max(0, mi.expected - afterAdding);

                              return (
                                <div key={idx} className={`rounded p-2 ${mi.isAiMatch ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
                                  {/* Product info with image and title */}
                                  <div className="flex items-start gap-2 mb-2">
                                    {/* Product image - prefer inbound image, fallback to scanned item image */}
                                    {(mi.matchData?.LARGE_IMG || mi.item.image_url) ? (
                                      <img
                                        src={mi.matchData?.LARGE_IMG || mi.item.image_url}
                                        alt={mi.matchData?.TITLE || mi.item.item_name}
                                        className="w-12 h-12 object-contain rounded border border-gray-200 flex-shrink-0 bg-white"
                                      />
                                    ) : (
                                      <div className="bg-gray-100 rounded w-12 h-12 flex items-center justify-center flex-shrink-0">
                                        <Box className="h-6 w-6 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      {/* Product title */}
                                      <p className="text-xs font-medium text-gray-800 line-clamp-2">
                                        {mi.matchData?.TITLE || mi.item.item_name || 'Unknown Product'}
                                      </p>
                                      {/* ASIN and AI match info */}
                                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <span className="text-[10px] text-gray-500 font-mono">
                                          {mi.item.asin}
                                          {mi.isAiMatch && mi.matchData?.ASIN && mi.matchData.ASIN !== mi.item.asin && (
                                            <span className="text-purple-600 ml-1">→ {mi.matchData.ASIN}</span>
                                          )}
                                        </span>
                                        {mi.isAiMatch && mi.aiMatchData && (
                                          <>
                                            <Sparkles className="h-3 w-3 text-purple-500" />
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                              mi.aiMatchData.confidence >= 0.9 ? 'bg-green-200 text-green-800' :
                                              mi.aiMatchData.confidence >= 0.7 ? 'bg-yellow-200 text-yellow-800' :
                                              'bg-orange-200 text-orange-800'
                                            }`}>
                                              {Math.round(mi.aiMatchData.confidence * 100)}%
                                            </span>
                                            {mi.aiMatchData.match_type === 'multipack' && (
                                              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                                {mi.aiMatchData.input_qty}→{mi.aiMatchData.output_qty}
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* Simplified quantity display */}
                                  <div className={`rounded-lg p-3 ${
                                    overReceiving ? 'bg-red-50 border-2 border-red-300' :
                                    willComplete ? 'bg-green-50 border-2 border-green-300' :
                                    'bg-gray-50 border border-gray-200'
                                  }`}>
                                    <div className="flex items-center gap-2 flex-wrap text-sm">
                                      {/* Current status */}
                                      <span className="font-medium text-gray-700">
                                        Current: <span className="font-bold text-gray-900">{mi.currentReceived}/{mi.expected}</span> units
                                      </span>

                                      {/* Arrow and adding */}
                                      <span className="text-blue-600 font-bold">→ adding {mi.qtyToAdd}</span>

                                      {/* New quantities */}
                                      <span className="text-gray-500">•</span>
                                      <span className={`font-medium ${
                                        overReceiving ? 'text-red-700' :
                                        willComplete ? 'text-green-700' : 'text-gray-700'
                                      }`}>
                                        new: <span className="font-bold">{afterAdding}/{mi.expected}</span>
                                      </span>

                                      {/* Pending/Complete status */}
                                      {overReceiving ? (
                                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">
                                          ⚠️ OVER BY {afterAdding - mi.expected}
                                        </span>
                                      ) : remaining > 0 ? (
                                        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">
                                          {remaining} still pending
                                        </span>
                                      ) : (
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
                                          <CheckCircle2 className="h-3 w-3" /> Complete
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Total Summary */}
                          <div className={`mt-3 p-3 rounded-lg ${
                            inbound.willBeComplete ? 'bg-green-100' : 'bg-orange-100'
                          }`}>
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold ${
                                inbound.willBeComplete ? 'text-green-800' : 'text-orange-800'
                              }`}>
                                Total for this Inbound:
                              </span>
                              <div className="flex items-center gap-2">
                                {inbound.willBeComplete ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                                ) : (
                                  <Clock className="h-5 w-5 text-orange-600" />
                                )}
                              </div>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                              <div className={inbound.willBeComplete ? 'text-green-700' : 'text-orange-700'}>
                                <span className="text-xs opacity-75">Now: </span>
                                <span className="font-bold">{inbound.totalCurrentReceived}</span>
                                <span className="text-xs opacity-75"> / {inbound.totalExpected}</span>
                              </div>
                              <div className={inbound.willBeComplete ? 'text-green-700' : 'text-orange-700'}>
                                <span className="text-xs opacity-75">After: </span>
                                <span className="font-bold">{inbound.totalAfterAdding}</span>
                                <span className="text-xs opacity-75"> / {inbound.totalExpected}</span>
                              </div>
                            </div>
                            {!inbound.willBeComplete && (
                              <div className="mt-2 text-sm text-orange-700 font-medium">
                                {inbound.remaining} units still pending
                              </div>
                            )}
                            {inbound.willBeComplete && (
                              <div className="mt-2 text-sm text-green-700 font-medium flex items-center gap-1">
                                <CheckCircle2 className="h-4 w-4" />
                                Will be fully received!
                              </div>
                            )}
                          </div>

                          {/* Add to Inbound Button */}
                          {addResult?.inbound_id === inbound.inbound_id ? (
                            <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded-lg">
                              <div className="flex items-center gap-2 text-green-800">
                                <CheckCircle2 className="h-5 w-5" />
                                <span className="font-semibold">
                                  Added {addResult.items_added} item(s) successfully!
                                </span>
                              </div>
                              {addResult.inbound_complete && (
                                <div className="mt-1 text-sm text-green-700">
                                  Inbound is now fully received!
                                </div>
                              )}
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleAddToInbound(inbound)}
                              disabled={submitting !== null}
                              className="w-full mt-3"
                              variant={isBestMatch ? 'primary' : 'outline'}
                            >
                              {submitting === inbound.inbound_id ? (
                                <>
                                  <LoadingSpinner size="sm" className="mr-2" />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add {inbound.totalToAdd} Units to This Inbound
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </div>

          {/* Tracking Badge */}
          <div className="text-center">
            <Badge variant="info" className="text-sm">
              Tracking: {searchResult.trackingCode}
            </Badge>
          </div>

          {/* Clear Button */}
          <div className="flex justify-center pt-2">
            <Button variant="outline" onClick={clearResults} className="px-8">
              Clear Results & Scan Next
            </Button>
          </div>
        </div>
      )}

      {/* Scan History */}
      {scanHistory.length > 0 && !searchResult && !loading && (
        <Card className="p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Scans</h3>
          <div className="space-y-2">
            {scanHistory.map((scan, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between py-2 px-3 bg-gray-50 rounded gap-2">
                <div className="flex items-center gap-3">
                  <Barcode className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="font-mono text-sm break-all">{scan.trackingCode}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 ml-7 sm:ml-0">
                  <span>{scan.clientName}</span>
                  <Badge variant="info">{scan.itemCount} item(s)</Badge>
                  <span className="text-gray-400">{scan.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!searchResult && !loading && !error && scanHistory.length === 0 && (
        <Card className="p-12 text-center">
          <Scan className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">Ready to Scan</h3>
          <p className="text-gray-500 mt-1">Scan a tracking barcode or type a tracking number to get started</p>
        </Card>
      )}
    </div>
  );
};

export default ReceivingScanner;
